// Diagnósticos CIE-10 reutilizables: typeahead rápido + modal "Códigos CIE"
// con búsqueda paginada y FAVORITOS por usuario (med_cie10_pagina /
// med_cie10_favorito, script 376). Lo usan Enfermedad Actual, los documentos
// de la ficha y el editor unificado de recetas.
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronLeft, ChevronRight, Loader2, Plus, Search, Star, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/Button';
import { getErrorMessage } from '@/lib/api/client';
import { Modal, fieldCls, medRpc, TIPO_DIAGNOSTICO } from './shared';
import type { Cie10, Diagnostico } from './shared';
import { LOCALE } from '@/lib/utils/regional';

// ---------------------------------------------------------------------------
// Diagnósticos: typeahead + modal "Códigos CIE" con favoritos y paginación
// ---------------------------------------------------------------------------
export function DiagnosticosCie({ dx, setDx }: { dx: Diagnostico[]; setDx: (d: Diagnostico[]) => void }) {
  const [q, setQ] = useState('');
  const [modal, setModal] = useState(false);
  const { data: resultados = [] } = useQuery<Cie10[]>({
    queryKey: ['med-cie10', q],
    queryFn: () => medRpc<Cie10[]>('med_cie10_buscar', { _q: q }),
    enabled: q.length >= 2,
  });
  const add = (d: Diagnostico) => {
    if (d.cie10_codigo && dx.some((x) => x.cie10_codigo === d.cie10_codigo)) {
      toast.info(`El diagnóstico ${d.cie10_codigo} ya está agregado`); setQ(''); return;
    }
    setDx([...dx, d]); setQ('');
  };
  return (
    <section className="space-y-2">
      {dx.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b bg-muted text-xs text-muted-foreground">
              <th className="py-1.5 px-2 text-left">Código</th><th className="py-1.5 px-2 text-left">CIE-10</th>
              <th className="py-1.5 px-2 text-center">Presuntivo</th><th className="py-1.5 px-2 text-center">Definitivo</th>
              <th className="py-1.5 px-2 text-left">Ppal</th><th className="py-1.5 px-2 text-right">Eliminar</th>
            </tr></thead>
            <tbody>
              {dx.map((d, i) => (
                <tr key={i} className="border-b last:border-0">
                  <td className="py-1.5 px-2 font-mono text-primary">{d.cie10_codigo ?? '—'}</td>
                  <td className="py-1.5 px-2 uppercase">{d.descripcion}</td>
                  {TIPO_DIAGNOSTICO.map((t) => (
                    <td key={t} className="py-1.5 px-2 text-center">
                      <input type="radio" className="accent-primary" name={`dx-tipo-${i}`} checked={d.tipo === t}
                        onChange={() => setDx(dx.map((x, j) => (j === i ? { ...x, tipo: t } : x)))} />
                    </td>
                  ))}
                  <td className="py-1.5 px-2">
                    <input type="checkbox" className="accent-primary" checked={d.es_principal}
                      onChange={(e) => setDx(dx.map((x, j) => ({ ...x, es_principal: j === i ? e.target.checked : false })))} />
                  </td>
                  <td className="py-1.5 px-2 text-right">
                    <button className="text-red-500" title="Quitar" aria-label="Quitar" onClick={() => setDx(dx.filter((_, j) => j !== i))}><Trash2 className="w-3.5 h-3.5" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className="flex gap-2 items-start">
        <Button size="sm" variant="outline" type="button" onClick={() => setModal(true)}><Plus className="w-3.5 h-3.5 mr-1" /> Agregar CIE</Button>
        <div className="relative max-w-md flex-1">
          <input className={fieldCls} placeholder="…o busca rápido por código o descripción" value={q} onChange={(e) => setQ(e.target.value)} />
          {resultados.length > 0 && (
            <ul className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-md border bg-card shadow-lg text-sm">
              {resultados.map((r) => (
                <li key={r.codigo}>
                  <button className="w-full text-left px-3 py-1.5 hover:bg-muted" onClick={() => add({ cie10_codigo: r.codigo, descripcion: r.descripcion, tipo: 'definitivo', es_principal: dx.length === 0 })}>
                    <b>{r.codigo}</b> · {r.descripcion}
                  </button>
                </li>
              ))}
              <li><button className="w-full text-left px-3 py-1.5 text-primary hover:bg-muted" onClick={() => add({ cie10_codigo: null, descripcion: q, tipo: 'presuntivo', es_principal: dx.length === 0 })}>+ Agregar «{q}» como diagnóstico local</button></li>
            </ul>
          )}
        </div>
      </div>
      {modal && <CieModal onPick={(codigo, descripcion) => { add({ cie10_codigo: codigo, descripcion, tipo: 'definitivo', es_principal: dx.length === 0 }); setModal(false); }} onClose={() => setModal(false)} />}
    </section>
  );
}

interface CiePag { total: number; rows: { codigo: string; descripcion: string; favorito: boolean }[] }

function CieModal({ onPick, onClose }: { onPick: (codigo: string, descripcion: string) => void; onClose: () => void }) {
  const qc = useQueryClient();
  const POR = 10;
  const [texto, setTexto] = useState('');
  const [q, setQ] = useState('');
  const [pagina, setPagina] = useState(1);
  const [soloFav, setSoloFav] = useState(false);

  const { data, isFetching } = useQuery<CiePag>({
    queryKey: ['med-cie10-pagina', q, pagina, soloFav],
    queryFn: () => medRpc<CiePag>('med_cie10_pagina', { _q: q, _pagina: pagina, _por: POR, _solo_fav: soloFav }),
  });
  const total = data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR));

  const favorito = useMutation({
    mutationFn: ({ codigo, fav }: { codigo: string; fav: boolean }) => medRpc('med_cie10_favorito', { _codigo: codigo, _fav: fav }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['med-cie10-pagina'] }),
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Códigos CIE" onClose={onClose} wide>
      <div className="space-y-3">
        <form className="flex gap-2 items-center" onSubmit={(e) => { e.preventDefault(); setQ(texto.trim()); setPagina(1); }}>
          <div className="relative flex-1">
            <input className={`${fieldCls} pr-8`} placeholder="Presionar 'Enter' para buscar…" autoFocus value={texto} onChange={(e) => setTexto(e.target.value)} />
            <Search className="w-4 h-4 absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          </div>
          <label className="flex items-center gap-1.5 text-xs cursor-pointer whitespace-nowrap">
            <input type="checkbox" className="accent-primary" checked={soloFav} onChange={(e) => { setSoloFav(e.target.checked); setPagina(1); }} /> Solo favoritos
          </label>
        </form>

        <div className="rounded-md border overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-muted">
              <tr className="text-left text-xs text-muted-foreground">
                <th className="py-2 px-3 w-16">Favoritos</th><th className="py-2 px-3 w-20">Código</th><th className="py-2 px-3">Descripción</th>
              </tr>
            </thead>
            <tbody>
              {isFetching && <tr><td colSpan={3} className="py-6 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto text-primary" /></td></tr>}
              {!isFetching && (data?.rows ?? []).length === 0 && <tr><td colSpan={3} className="py-6 text-center text-muted-foreground">Sin resultados</td></tr>}
              {!isFetching && (data?.rows ?? []).map((r) => (
                <tr key={r.codigo} className="border-t hover:bg-primary/5">
                  <td className="py-1.5 px-3">
                    <button type="button" title={r.favorito ? 'Quitar de favoritos' : 'Marcar favorito'} aria-label="Favorito"
                      onClick={() => favorito.mutate({ codigo: r.codigo, fav: !r.favorito })}>
                      <Star className={`w-4 h-4 ${r.favorito ? 'fill-amber-400 text-amber-400' : 'text-muted-foreground'}`} />
                    </button>
                  </td>
                  <td className="py-1.5 px-3 font-mono text-primary cursor-pointer" onClick={() => onPick(r.codigo, r.descripcion)}>{r.codigo}</td>
                  <td className="py-1.5 px-3 cursor-pointer" onClick={() => onPick(r.codigo, r.descripcion)}>{r.descripcion}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <span>Mostrando {(data?.rows?.length ?? 0) > 0 ? (pagina - 1) * POR + 1 : 0} a {(pagina - 1) * POR + (data?.rows?.length ?? 0)} de {total.toLocaleString(LOCALE)} registros</span>
          <span className="flex items-center gap-1">
            <button type="button" className="rounded border p-1 disabled:opacity-40" disabled={pagina <= 1} onClick={() => setPagina((p) => p - 1)} title="Anterior" aria-label="Anterior"><ChevronLeft className="w-4 h-4" /></button>
            <span className="px-2">{pagina} / {paginas.toLocaleString(LOCALE)}</span>
            <button type="button" className="rounded border p-1 disabled:opacity-40" disabled={pagina >= paginas} onClick={() => setPagina((p) => p + 1)} title="Siguiente" aria-label="Siguiente"><ChevronRight className="w-4 h-4" /></button>
          </span>
        </div>

        <div className="flex justify-end"><Button variant="outline" size="sm" onClick={onClose}>Cerrar</Button></div>
      </div>
    </Modal>
  );
}
